import cloneDeep from 'lodash/cloneDeep';

import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';
import { generateFormObject } from '@/core/tools/forms/generate_form_object';
import { prepareFormObjectForSave } from '@/core/tools/forms/prepare_form_object_for_save';
import numeral from 'numeral';

interface ShiftAtomFactFormState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    document: any;
    shift_plans: any[];
    form: any;
    schema: any;
    storages: any[];
    targetForDelete: any[];

    meta: any;
}

const state: ShiftAtomFactFormState = {
    loading: false,
    loaded: false,
    fatalError: false,

    document: null,
    shift_plans: [],
    storages: [],
    targetForDelete: [],
    form: null,
    schema: null,

    meta: {},
};

const initState: ShiftAtomFactFormState = cloneDeep(state);

export const BASES_MANAGER_DATA = 'GET_BASES_MANAGER_DATA';
export const BASES_MANAGER_SCHEMA = 'GET_BASES_MANAGER_SCHEMA';
export const BASES_MANAGER_STORAGES = 'BASES_MANAGER_STORAGES';

export const actions: ActionTree<ShiftAtomFactFormState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE')
    },

    init({ commit, dispatch }, params) {
        return dispatch('loadData', params);
    },

    loadData({ commit }, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(BASES_MANAGER_DATA)
                    .raw('documents.get')
                    .action({
                        id: params.id
                    }),
            ]).then((result: ServerResult) => {
                const _res = result.getOperation(BASES_MANAGER_DATA).getContent()
                commit('SET_FORM_DATA', {
                    document: _res.document,
                    schema: _res.schema,
                });

                commit('LOADED_SUCCESS');
                resolve(null);
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    prepareNew({ commit, state }, initParams) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(BASES_MANAGER_SCHEMA)
                    .raw('schemas.get')
                    .action({
                        id: `s:shift_fact_atom`
                    }),
            ]).then((result: ServerResult) => {
                commit('GENERATE_FORM_DATA', {
                    ...result.getOperation(BASES_MANAGER_SCHEMA).getContent(),
                    initParams
                });

                commit('LOADED_SUCCESS');
                resolve();
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    saveForm({ commit }, documentId) {
        const vm = (this as any)._vm;

        const normalizedForm = prepareFormObjectForSave(state.schema, state.form);

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            let saveQuery = null;

            if (documentId) {
                saveQuery = vm.$q('BASES_MANAGER_DATA_SAVE')
                    .update(documentId, normalizedForm)
            } else {
                saveQuery = vm.$q('BASES_MANAGER_DATA_SAVE')
                    .create('s:shift_fact_atom', normalizedForm)
            }

            vm.$db([
                saveQuery
            ]).then((result: ServerResult) => {
                const operationResult = result.getOperation('BASES_MANAGER_DATA_SAVE');

                if (operationResult.hasError()) {
                    console.log('error', operationResult.getContent());

                    commit('LOADED_SUCCESS');
                    reject();
                } else {
                    commit('ON_FORM_SAVE', operationResult.getContent())

                    commit('LOADED_SUCCESS');
                    resolve(operationResult.getContent());
                }
            })
            .catch((error: any) => {
                console.error('errror', error)
                reject(error);
            });
        })
    },
    changeFormValue({commit}, {field, newValue}) {
        commit('CHANGE_VALUE', {field, newValue});
    },

    changeStorageValue({ state }, { index, field, newValue}) {
        const _sum = state.storages.reduce((prev: any, item: any, idx) => {
            return prev + (idx == index ? 0 : parseInt(item.quantity))
        }, 0)
        switch (field) {
            case 'quantity':
                newValue = parseInt(newValue)
                if (_sum + newValue > state.form.quantity) {
                    newValue = state.form.quantity - _sum;
                }
                state.storages[index][field] = newValue;
                break

            default:
                state.storages[index][field] = newValue;
        }
    },

    addAttachments({state, dispatch}, {newValue}) {
      state.form.attachments = [
        newValue,
        ...(state.form.attachments ? state.form.attachments : []),
      ]
    },

};

export const mutations: MutationTree<ShiftAtomFactFormState> = {
    RESET_STATE: (state) => {
        for(const key in initState) {
            (state as any)[key] = (initState as any)[key]
        }
    },

    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },

    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },

    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_BASES_MANAGER_DATA(state, {document}) {
        state.document = document;
    },

    SET_FORM_DATA(state, {document, schema}) {
        state.document = document;
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, state.document);
    },

    GENERATE_FORM_DATA(state, {schema, initParams}) {
        state.document = {};
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, initParams);
    },

    ON_FORM_SAVE(state, {document, schema}) {
        state.document = document;
        state.schema = schema;

        state.form = generateFormObject(schema, state.document);
    },

    CHANGE_VALUE(state: ShiftAtomFactFormState, {field, newValue}) {
        const schema_field = state.schema.fields.filter((item: any) => {
            return item.code == field
        })[0]

        const calc = (form: any) => {
            console.log(form.container_weight, form.quantity)
            if (form.quantity && form.container_weight) {
                form.fact_weight = Math.round(parseFloat(form.container_weight) * parseFloat(form.quantity) * 100) / 100
            }
        }

        if (schema_field && (schema_field.type == 'decimal' || schema_field.type == 'integer')) {
            console.log('newValue', newValue)
            newValue = parseFloat(newValue.replace(',', '.'))
            console.log('newValue', newValue)
        }
        switch (field) {
            case 'quantity':
                // if (newValue > 140) {
                //     newValue = 140;
                // }
                state.form[field] = newValue;
                calc(state.form)
                break

            case 'container_weight':
                state.form[field] = newValue;
                calc(state.form)
                break

            default:
                state.form[field] = newValue;
        }
    },
};

const getters = {
    fields: (state: ShiftAtomFactFormState) => {
        return state.schema.fields.reduce((prev: any, field: any) => {
            prev[field.code] = field;
            return prev;
        }, {});
    },
};

export const STORE_KEY = '$_shift_atom_fact_form';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
